import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useApiClient } from "Services/API/useApiClient";
import { AccountClient, isLogonSuccessResponseTwoFactorConfirmationRequired, isLogonSuccessResponseTwoFactorRegistrationRequired, LogonSuccessResponse, RegisterSuccessResponse } from "Services/LegacyAPI/AccountClient";
import { isLocalUrl } from "../../../Utils/isLocalUrl";
import { AdfsForm } from "./AdfsForm";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { LoginForm, LoginFormModel } from "./LoginForm";
import { RegisterForm } from "./RegisterForm";
import { SigningEmailProps, SigningSuccessData } from "./SigningCommon";
import { TwoFactorConfirmForm } from "./TwoFactorConfirmForm";
import { TwoFactorRegisterForm } from "./TwoFactorRegisterForm";
import { ChangeEmailForm } from "./ChangeEmailForm";

export interface SigningFormProps {
   isModal?: boolean,
   mode?: LSStore.SigningMode;
   returnUrl?: string,
   initialEmail?: string,
   registerFormProps?: LSStore.SigningRegisterProps,
   loginFormProps?: LSStore.SigningLoginProps,
   changePasswordFormProps?: LSWebUI.Models.ForgotPasswordViewModel,
   className?: string;
   registerModel?: LSWebUI.Models.RegisterModel;
   changeEmailFormProps?: LSWebUI.Models.ChangeEmailModel; 
   closeModal?: () => void;
}

export const SigningForm: React.FC<SigningFormProps> = function ({ className, registerModel, mode = LSStore.SigningMode.Login, initialEmail, returnUrl, closeModal, loginFormProps, registerFormProps, changePasswordFormProps, changeEmailFormProps, isModal = false }) {
   const [activeMode, setActiveMode] = useState<LSStore.SigningMode>(mode);
   const [previousMode, setPreviousMode] = useState<LSStore.SigningMode>(mode);   
   const [email, setEmail] = useState<string | undefined>(initialEmail || undefined);
   const [adfsProviders, setAdfsProviders] = useState<LSCommon.Context.AdfsProvider[]>([]);
   const [matchingAdfsProvider, setMatchingAdfsProvider] = useState<LSCommon.Context.AdfsProvider | undefined>(undefined);
   const signingModalState = useSelector(state => state.signingModal);
   
   const [lstoken, setLstoken] = useState<string>("");
   const [phoneNumberLast4, setPhoneNumberLast4] = useState<string>("");   

   const [loginProps, setLoginProps] = useState<LSStore.SigningLoginProps>(loginFormProps || {});
   const [registerProps, setRegisterProps] = useState<LSStore.SigningRegisterProps>(registerFormProps || {});
   const [changePasswordProps, setChangePasswordProps] = useState<LSWebUI.Models.ForgotPasswordViewModel | undefined>(changePasswordFormProps);

   const signingEmailProps: SigningEmailProps = {
      email: email,
      onEmailChange: setEmail
   };   

   const accountClient = useApiClient(AccountClient);
   
   const modeClassName = (m: LSStore.SigningMode) => {
      return classNames("signing-mode", { "signing-mode--active": activeMode == m });
   }
   const formTransition = (): React.CSSProperties | undefined => {
      const multiplier = activeMode;
      if (multiplier > 0)
         return { transform: `translateX(-${100 * multiplier}%)` };
      else
         return undefined;
   }

   const selectModeWithHistory = (selectedMode: LSStore.SigningMode) => {
      setPreviousMode(activeMode);
      setActiveMode(selectedMode);
   }

   const onLoginSuccess = (logonResult: LogonSuccessResponse, model?: LoginFormModel): boolean => {
      if (isLogonSuccessResponseTwoFactorRegistrationRequired(logonResult)) {
         setLstoken(logonResult.lstoken);
         selectModeWithHistory(LSStore.SigningMode.TwoFactorRegister);
      } else if (isLogonSuccessResponseTwoFactorConfirmationRequired(logonResult)) {
         setLstoken(logonResult.lstoken);
         setPhoneNumberLast4(logonResult.phoneNumberLast4);
         selectModeWithHistory(LSStore.SigningMode.TwoFactorConfirm);
      } else {         
         onSigningSuccess({
            userId: logonResult.userId,
            fullName: logonResult.fullName,
         });            
         return true;         
      }
      return false;
   }
   const onRegisterSuccess = (registerResponse: RegisterSuccessResponse) => {
      if (registerResponse.showTwoFactorForm && registerResponse.data) {
         setLstoken(registerResponse.data.TwoFactorConfirmModel.LSToken || "");
         if (registerResponse.data.InitialMode == "tfRegister") {
            selectModeWithHistory(LSStore.SigningMode.TwoFactorRegister);
         } else {
            setPhoneNumberLast4(registerResponse.data.TwoFactorConfirmModel.PhoneNumberLast4 || "");
            selectModeWithHistory(LSStore.SigningMode.TwoFactorConfirm);
         }
      } else {
         onSigningSuccess({
            userId: registerResponse.userId || null,
            fullName: registerResponse.fullName || null,
            joinFlow: true
         });
         return true;
      }    

      return false;
   }

   const onSigningSuccess = (data?: SigningSuccessData) => {
      let ssoRedirectUrl: string | undefined;
      if (data) {
         User.id = data.userId;
         ssoRedirectUrl = data.redirectUrl;
      }

      if (isModal) {
         if (data && LSHelper.Account.onLogInSuccessCallback && typeof LSHelper.Account.onLogInSuccessCallback === "function")
            LSHelper.Account.onLogInSuccessCallback(data.userId!, data.fullName, data.joinFlow || false, signingModalState.caller);

         if (data && data.userId) {
            //$(document).trigger("userAuthenticated", [data.userId, data.joinFlow]);
            document.dispatchEvent(new CustomEvent("userAuthenticated"));
         }

         if (window.EMBEDDEDISLAND) {
            LSHelper.Account.reloadHeader();
            if (closeModal)
               closeModal();            
         } else if (!signingModalState.onlyHeaderReload) {
            if (signingModalState.popUpRedirectUrl || ssoRedirectUrl)
               window.location.href = ssoRedirectUrl || signingModalState.popUpRedirectUrl || window.location.href;
            else {
               window.location.reload();
            }
         } else {
            LSHelper.Account.reloadHeader();
            if(closeModal)
               closeModal();
         }
      }
      else {
         const urlParams = new URLSearchParams(window.location.search);         
         let returnUrlToUse = returnUrl || urlParams.get("ReturnUrl") || urlParams.get("returnUrl") || ssoRedirectUrl;
         if (!returnUrlToUse || !isLocalUrl(returnUrlToUse)) {
            returnUrlToUse = "/my/index";
         }
         window.location.href = returnUrlToUse;
      }      
   }

   const onSwtichToChangePassword = (changePasswordOptions: LSWebUI.Models.ForgotPasswordViewModel) => {
      setChangePasswordProps(changePasswordOptions);
      selectModeWithHistory(LSStore.SigningMode.ChangePassword);
   }

   useEffect(() => {
      document.SSO_logon_Completed = function (data) {         
         onSigningSuccess({
            userId: data.userId,
            fullName: data.firstName,
            redirectUrl: data.redirectUrl ?? undefined,
         });
      };

      const accountLinkingListener = (ev: SSOAccountLinkingCompletedEvent) => {
         onSigningSuccess();
      }

      document.addEventListener("SSO_accountLinking_Completed", accountLinkingListener);
      return () => {
         delete document.SSO_logon_Completed;
         document.removeEventListener("SSO_accountLinking_Completed", accountLinkingListener);
      }
   }, [])

   useEffect(() => {
      if (isModal) {
         if (signingModalState.initialEmail)
            setEmail(signingModalState.initialEmail || undefined);
         if (signingModalState.loginProps) {
            setLoginProps(signingModalState.loginProps);
         }
         if (signingModalState.registerProps) {
            setRegisterProps(signingModalState.registerProps);
         }
      }
   }, [isModal])

   const getAdfsProvides = async (abortSignal: AbortSignal) => {
      try {
         setAdfsProviders(await accountClient.adfsProviders(abortSignal));
      } catch { }
   }

   useEffect(() => {
      const abortController = new AbortController();
      getAdfsProvides(abortController.signal);

      return function cleanup() {
         abortController.abort();
      }
   }, [])

   useEffect(() => {
      if (email)
         for (let i = 0; i < adfsProviders.length; i++)
            for (let j = 0; j < adfsProviders[i].EmailDomains.length; j++) {
               if (email.match(new RegExp("@" + adfsProviders[i].EmailDomains[j] + "$", "i"))) {
                  setMatchingAdfsProvider(adfsProviders[i]);
                  if (activeMode != LSStore.SigningMode.Adfs)
                     selectModeWithHistory(LSStore.SigningMode.Adfs);
                  return;
               }
            }
      setMatchingAdfsProvider(undefined);
      if (activeMode == LSStore.SigningMode.Adfs)
         selectModeWithHistory(previousMode);
   }, [email]);   
   return (
      <div className={classNames("signing-form", className)} >
         <div className="signing-form-inner" style={formTransition()}>
            {/* Note that modes go in the same order they are defined in the enum */}
            <div className={modeClassName(LSStore.SigningMode.Register)}>
               <RegisterForm isActive={activeMode == LSStore.SigningMode.Register} onRegisterSuccess={onRegisterSuccess} onSwitchToLogin={() => selectModeWithHistory(LSStore.SigningMode.Login)} registerModel={registerModel} {...signingEmailProps} registerProps={registerProps} />
            </div>
            <div className={modeClassName(LSStore.SigningMode.Login)}>
               <LoginForm isActive={activeMode == LSStore.SigningMode.Login} onLoginSuccess={onLoginSuccess} onSwitchToSignup={() => selectModeWithHistory(LSStore.SigningMode.Register)} onSwitchToForgotPassword={() => selectModeWithHistory(LSStore.SigningMode.Forgot)} onSwtichToChangePassword={onSwtichToChangePassword} {...signingEmailProps} {...loginProps} />
            </div>
            <div className={modeClassName(LSStore.SigningMode.Forgot)}>
               <ForgotPasswordForm isActive={activeMode == LSStore.SigningMode.Forgot} onSwitchBack={() => selectModeWithHistory(previousMode)} {...signingEmailProps} />
            </div>
            <div className={modeClassName(LSStore.SigningMode.Adfs)}>
               <AdfsForm isActive={activeMode == LSStore.SigningMode.Adfs} adfsProvider={matchingAdfsProvider} {...signingEmailProps} />
            </div>
            <div className={modeClassName(LSStore.SigningMode.TwoFactorRegister)}>
               <TwoFactorRegisterForm isActive={activeMode == LSStore.SigningMode.TwoFactorRegister} email={email || ""} lstoken={lstoken} onSuccess={onLoginSuccess}/>
            </div>
            <div className={modeClassName(LSStore.SigningMode.TwoFactorConfirm)}>
               <TwoFactorConfirmForm isActive={activeMode == LSStore.SigningMode.TwoFactorConfirm} email={email || ""} lstoken={lstoken} phoneNumberLast4={phoneNumberLast4} onSuccess={onLoginSuccess} />
            </div>
            <div className={modeClassName(LSStore.SigningMode.ChangePassword)}>
               { changePasswordProps && <ChangePasswordForm isActive={activeMode == LSStore.SigningMode.ChangePassword} {...changePasswordProps} returnUrl={returnUrl} onSuccess={onLoginSuccess} /> }
            </div>
            <div className={modeClassName(LSStore.SigningMode.ChangeEmail)}>
               { changeEmailFormProps && <ChangeEmailForm isActive={activeMode == LSStore.SigningMode.ChangeEmail} {...changeEmailFormProps} returnUrl={returnUrl} /> }
            </div>
         </div>
      </div>
   )
}